import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fragment, useEffect } from "react";
import { useFieldArray } from "react-hook-form";

export const AttributeField = ({ field, index, executionMode, control, register, errors, removeAttribute, getValues, setDeletedChildAttributeIds }) => {
    const { fields: childFields, append: appendChild, remove: removeChild } = useFieldArray({
        control,
        name: `attributes.${index}.childAttributes`
    });
    const attributeType = getValues(`attributes.${index}.attr_type`);

    const handleRemoveChildAttribute = (childIndex) => {
        const childAttributeToDelete = getValues(`attributes.${index}.childAttributes.${childIndex}`);
        if (childAttributeToDelete?.attributeId) {
            setDeletedChildAttributeIds((prev) => [...prev, childAttributeToDelete.attributeId]);
        }
        removeChild(childIndex);
    };

    // Automatically add the first child attribute if type = "object"
    useEffect(() => {
        if (attributeType === "object" && childFields.length === 0) {
            appendChild({
                name: "",
                description: "",
                attr_type: "",
                required: "false",
                source: "model",
                default_value: "",
                aliases: "",
            });
        }
    }, [attributeType, childFields.length, appendChild]);


    return (
        <div className="mb-2 border rounded p-2">
            <div className="d-flex justify-content-center align-items-center">
                <div className='col-5'>
                    <hr className='border border-secondary opacity-25 w-100' />
                </div>
                <div className='col-2 text-center'>
                    <span className='fs-6 fw-bold'>Attribute {index + 1}</span>
                </div>
                <div className='col-5'>
                    <hr className='border border-secondary opacity-25 w-100' />
                </div>
            </div>
            {/* Parent Attribute Fields */}
            <div className='d-flex align-items-center gap-2 mt-2'>
                {/* Name */}
                <div className="col-3">
                    <label className="form-label"><span className="text-danger">*</span> Name</label>
                    <input
                        type="text"
                        placeholder="Enter name"
                        className={`form-control px-2 ${errors?.attributes?.[index]?.name ? "is-invalid" : ""}`}
                        {...register(`attributes.${index}.name`,
                            {
                                required: true,
                                maxLength: { value: 255, message: "Max 255 characters allowed" },
                                validate: (value) => {
                                    const trimmedValue = value.trim();
                                    if (/\s/.test(trimmedValue)) return "Spaces are not allowed. Use underscores instead";
                                    if (trimmedValue.length === 0) {
                                        return "Name cannot be just spaces";
                                    }
                                }
                            }
                        )}
                        onKeyDown={(e) => e.key === " " && e.preventDefault()}
                    />
                </div>

                {/* Description */}
                <div className="col-3">
                    <label className="form-label"><span className="text-danger">*</span> Description</label>
                    <input
                        type="text"
                        placeholder="Enter description"
                        className={`form-control px-2 ${errors?.attributes?.[index]?.description ? "is-invalid" : ""}`}
                        {...register(`attributes.${index}.description`, { required: true })}
                    />
                </div>

                {/* Attribute Type */}
                <div className="col-2">
                    <label className="form-label"><span className="text-danger">*</span> Attribute Type</label>
                    <select
                        className={`form-select px-2 ${errors?.attributes?.[index]?.attr_type ? "is-invalid" : ""}`}
                        {...register(`attributes.${index}.attr_type`, { required: true })}
                    >
                        <option value="">Select type</option>
                        <option value="string">String</option>
                        <option value="integer">Integer</option>
                        <option value="text">Text</option>
                        <option value="object">Object</option>
                    </select>
                </div>

                {/* Required */}
                <div className="col-2">
                    <label className="form-label"><span className="text-danger">*</span> Required</label>
                    <select
                        className={`form-select px-2 ${errors?.attributes?.[index]?.required ? "is-invalid" : ""}`}
                        {...register(`attributes.${index}.required`, { required: true })}
                    >
                        <option value="">Select...</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>

                {/* Delete Attribute */}
                <div className="col-1">
                    <span
                        title='Delete attribute'
                        className="ms-2 text-danger cursor-pointer fs-6 d-block mt-4"
                        onClick={() => removeAttribute(index)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </span>
                </div>
            </div>
            <div className="d-flex align-items-center gap-2">
                <div className="col-3">
                    {errors?.attributes?.[index]?.name && <div className="invalid-feedback d-block">{errors?.attributes?.[index]?.name.message}</div>}
                </div>
            </div>
            {executionMode === "stateful" && (
                <div className="d-flex align-items-center gap-2 mt-2">
                    <div className="col-2">
                        <label className="form-label">Source</label>
                        <select
                            className={`form-select px-2 ${errors?.attributes?.[index]?.source ? "is-invalid" : ""}`}
                            {...register(`attributes.${index}.source`, { required: true })}
                        >
                            <option value="model">From model</option>
                            <option value="constant">Constant</option>
                        </select>
                    </div>
                    <div className="col-4">
                        <label className="form-label">Default Value</label>
                        <input
                            type="text"
                            placeholder="Optional default / constant value"
                            className={`form-control px-2 ${errors?.attributes?.[index]?.default_value ? "is-invalid" : ""}`}
                            {...register(`attributes.${index}.default_value`, {
                                validate: (value) => {
                                    const source = getValues(`attributes.${index}.source`);
                                    if (source === "constant" && String(value || "").trim().length === 0) {
                                        return "Default value is required for constant source";
                                    }
                                    if (String(value || "").length > 500) {
                                        return "Max 500 characters allowed";
                                    }
                                    return true;
                                }
                            })}
                        />
                    </div>
                    <div className="col-5">
                        <label className="form-label">Aliases</label>
                        <input
                            type="text"
                            placeholder="Comma-separated aliases"
                            className={`form-control px-2 ${errors?.attributes?.[index]?.aliases ? "is-invalid" : ""}`}
                            {...register(`attributes.${index}.aliases`, {
                                validate: (value) => String(value || "").length <= 500 || "Max 500 characters allowed",
                            })}
                        />
                    </div>
                </div>
            )}

            {/* Child Attributes */}
            {attributeType === "object" && (
                <div className="mt-3 ps-3 border-start">
                    {childFields.map((child, childIndex) => (
                        <Fragment key={child.id}>
                            <div key={child.id} className="d-flex align-items-center gap-2 mt-2">
                                <div className="col-3">
                                    <label className="form-label"><span className="text-danger">*</span> Name</label>
                                    <input
                                        type="text"
                                        placeholder="Child name"
                                        className={`form-control px-2 ${errors?.attributes?.[index]?.childAttributes?.[childIndex]?.name ? "is-invalid" : ""}`}
                                        {...register(`attributes.${index}.childAttributes.${childIndex}.name`, {
                                            required: true,
                                            maxLength: { value: 255, message: "Max 255 characters allowed" },
                                            validate: (value) => {
                                                const trimmedValue = value.trim();
                                                if (/\s/.test(trimmedValue)) return "Spaces are not allowed. Use underscores instead";
                                                if (trimmedValue.length === 0) {
                                                    return "Name cannot be just spaces";
                                                }
                                            }
                                        })}
                                        onKeyDown={(e) => e.key === " " && e.preventDefault()}
                                    />
                                </div>
                                <div className="col-3">
                                    <label className="form-label"><span className="text-danger">*</span> Description</label>
                                    <input
                                        type="text"
                                        placeholder="Child description"
                                        className={`form-control px-2 ${errors?.attributes?.[index]?.childAttributes?.[childIndex]?.description ? "is-invalid" : ""}`}
                                        {...register(`attributes.${index}.childAttributes.${childIndex}.description`, { required: true })}
                                    />
                                </div>
                                <div className="col-2">
                                    <label className="form-label"><span className="text-danger">*</span> Attribute Type</label>
                                    <select
                                        className={`form-select px-2 ${errors?.attributes?.[index]?.childAttributes?.[childIndex]?.attr_type ? "is-invalid" : ""}`}
                                        {...register(`attributes.${index}.childAttributes.${childIndex}.attr_type`, { required: true })}
                                    >
                                        <option value="">Select type</option>
                                        <option value="string">String</option>
                                        <option value="integer">Integer</option>
                                        <option value="text">Text</option>
                                        <option value="object">Object</option>
                                    </select>
                                </div>
                                <div className="col-2">
                                    <label className="form-label"><span className="text-danger">*</span> Required</label>
                                    <select
                                        className={`form-select px-2 ${errors?.attributes?.[index]?.childAttributes?.[childIndex]?.required ? "is-invalid" : ""}`}
                                        {...register(`attributes.${index}.childAttributes.${childIndex}.required`, { required: true })}
                                    >
                                        <option value="">Select...</option>
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </select>
                                </div>
                                <div className="col-1">
                                    <span
                                        title="Delete child"
                                        className="ms-2 text-danger d-block mt-4 cursor-pointer"
                                        onClick={() => handleRemoveChildAttribute(childIndex)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div className="col-3">
                                    {errors?.attributes?.[index]?.childAttributes?.[childIndex]?.name && <div className="invalid-feedback d-block">{errors?.attributes?.[index]?.childAttributes?.[childIndex]?.name.message}</div>}
                                </div>
                            </div>
                            {executionMode === "stateful" && (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                    <div className="col-2">
                                        <label className="form-label">Source</label>
                                        <select
                                            className={`form-select px-2 ${errors?.attributes?.[index]?.childAttributes?.[childIndex]?.source ? "is-invalid" : ""}`}
                                            {...register(`attributes.${index}.childAttributes.${childIndex}.source`, { required: true })}
                                        >
                                            <option value="model">From model</option>
                                            <option value="constant">Constant</option>
                                        </select>
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label">Default Value</label>
                                        <input
                                            type="text"
                                            placeholder="Optional default / constant value"
                                            className={`form-control px-2 ${errors?.attributes?.[index]?.childAttributes?.[childIndex]?.default_value ? "is-invalid" : ""}`}
                                            {...register(`attributes.${index}.childAttributes.${childIndex}.default_value`, {
                                                validate: (value) => {
                                                    const source = getValues(`attributes.${index}.childAttributes.${childIndex}.source`);
                                                    if (source === "constant" && String(value || "").trim().length === 0) {
                                                        return "Default value is required for constant source";
                                                    }
                                                    if (String(value || "").length > 500) {
                                                        return "Max 500 characters allowed";
                                                    }
                                                    return true;
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="col-5">
                                        <label className="form-label">Aliases</label>
                                        <input
                                            type="text"
                                            placeholder="Comma-separated aliases"
                                            className={`form-control px-2 ${errors?.attributes?.[index]?.childAttributes?.[childIndex]?.aliases ? "is-invalid" : ""}`}
                                            {...register(`attributes.${index}.childAttributes.${childIndex}.aliases`, {
                                                validate: (value) => String(value || "").length <= 500 || "Max 500 characters allowed",
                                            })}
                                        />
                                    </div>
                                </div>
                            )}
                        </Fragment>

                    ))}

                    <span
                        className='simple-btn mt-3 d-inline-block'
                        onClick={() => appendChild({
                            name: "",
                            description: "",
                            attr_type: "",
                            required: "false",
                            source: "model",
                            default_value: "",
                            aliases: "",
                        })}
                    >
                        + Add Child Attribute
                    </span>
                </div>
            )}
        </div>
    );
};
